/**
 * Whether an order line displays ITEMIZED — its BASE price on the item row, the
 * modifier upcharges beneath it, and a closing "Item total" — and the numbers
 * to show when it does.
 *
 * 🔴 WHY THIS EXISTS. Every surface showed the line total INCLUDING modifiers
 * on the item row and then listed each modifier with its own "+$" again:
 *
 *      1  PHO "SIFU"                 $33.50
 *           MENMA(BAMBOO SHOOTS)     +$2.00
 *           Shiitake                 +$3.00   ...
 *
 * House of Pho, 2026-09-23: the base price is $24.00, and the owner, the staff
 * and the guest all read that as $33.50 PLUS another $9.50. Itemized, the
 * column adds up top to bottom:
 *
 *      1  PHO "SIFU"                 $24.00
 *           MENMA(BAMBOO SHOOTS)     +$2.00   ...
 *           Item total               $33.50
 *
 * PAPER vs SCREEN. The screens (POS order panel, management Order Details,
 * checkout, the ordering cart) close an itemized line on the "Item total" row
 * this returns a label for. Receipts do NOT: a total row shipped on paper and
 * was removed the next day as "too busy, too many numbers" (owner,
 * 2026-09-24), so the receipt composer uses only the base price from here.
 *
 * ONE RULE ON EVERY SURFACE. This is the TypeScript copy of a rule that also
 * lives in Go (`itemizedReceiptLine`, order-up-gateway
 * internal/renderer/escpos_format.go) and Dart (`ReceiptItemization`,
 * order-up-pos lib/utils/receipt/item_itemization.dart). Which one draws a
 * given receipt depends on how the till reached the printer, and the owner
 * compares the till, the paper and the back office side by side — so the three
 * must decide identically. Change one, change all three.
 *
 * A line itemizes only when `(unit + Σ modifier) × qty − discount == total`,
 * compared in whole minor units. That check is the point, not a nicety: a
 * caller whose total folds in something the modifier list does not show (a
 * nested modifier the total skips, a split portion, an older payload with no
 * unit price) would otherwise display a column that does NOT add up — the very
 * confusion this removes. Anything that fails it, and any line with no priced
 * modifier, displays exactly as it always has.
 *
 * `modifierMinor` is each displayed modifier's amount PER UNIT of the item,
 * already extended for the modifier's own count (a 2x Tamago at $3.50 is one
 * 700) — pass exactly the amounts the surface prints beside the modifiers.
 */

export interface LineItemizationInput {
  readonly quantity: number;
  /** Per-unit base price, in minor units (cents). */
  readonly unitMinor: number;
  /** Every modifier amount the surface displays, per unit, in minor units. */
  readonly modifierMinor: readonly number[];
  /** The line's total as the surface displays it, in minor units. */
  readonly totalMinor: number;
  /** Line-level discount already subtracted from `totalMinor`, if any. */
  readonly discountMinor?: number;
  readonly voided?: boolean;
}

export interface LineItemization {
  readonly quantity: number;
  /** The base price the item row shows instead of the line total. */
  readonly unitMinor: number;
  /**
   * The base is per unit of a multi-quantity line, so the item row must say
   * "ea" — otherwise $24.00 beside a quantity of 2 reads as the price of both.
   */
  readonly perUnit: boolean;
  /** "Item total", or "Item total (x2)" for a multiple. */
  readonly totalLabel: string;
}

/** The closing row's label — the same words the gateway and the till print. */
export function itemTotalLabel(quantity: number): string {
  return quantity > 1 ? `Item total (x${quantity})` : 'Item total';
}

/** The item row's value suffix for a multi-quantity itemized line. */
export const PER_UNIT_SUFFIX = ' ea';

/**
 * Dollars (a float) to whole minor units, for the surfaces that hold money as
 * dollars. Non-finite reads as zero so the equality check fails closed.
 */
export function dollarsToMinor(dollars: number): number {
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
}

/** Decides itemization. `null` means "display it the way it always has". */
export function itemizeLine(input: LineItemizationInput): LineItemization | null {
  const { quantity, unitMinor, modifierMinor, totalMinor } = input;
  if (input.voided || !Number.isInteger(quantity) || quantity < 1) return null;
  if (!modifierMinor.some((m) => m !== 0)) return null;
  const each = modifierMinor.reduce((sum, m) => sum + m, unitMinor);
  if (each * quantity - (input.discountMinor ?? 0) !== totalMinor) return null;
  return {
    quantity,
    unitMinor,
    perUnit: quantity > 1,
    totalLabel: itemTotalLabel(quantity),
  };
}

/**
 * The shape of a cart line (`CartItem`, @order-up/models) as far as the rule
 * needs it — structural so the pure helper takes no class dependency.
 */
export interface CartLineLike {
  readonly quantity: number;
  /** Base price per unit, the selected size included. */
  readonly itemPrice: number;
  /** (itemPrice + every modifier) × quantity. */
  readonly totalPrice: number;
  readonly modifierGroups: readonly {
    readonly modifiers: readonly { readonly priceDelta: number; readonly totalPriceDelta: number }[];
  }[];
}

/**
 * `itemizeLine` for a cart line, in the amounts the ordering and checkout
 * carts DISPLAY: each upcharge (`priceDelta > 0`) as its `totalPriceDelta`
 * (per-instance price × the modifier's own count). A cart hides deductions, so
 * a line carrying one does not add up and keeps its single price.
 */
export function itemizeCartLine(item: CartLineLike): LineItemization | null {
  return itemizeLine({
    quantity: item.quantity,
    unitMinor: dollarsToMinor(item.itemPrice),
    modifierMinor: item.modifierGroups.flatMap((g) =>
      g.modifiers.filter((m) => m.priceDelta > 0).map((m) => dollarsToMinor(m.totalPriceDelta)),
    ),
    totalMinor: dollarsToMinor(item.totalPrice),
  });
}
